/**
 * App 路由壳层
 *
 * 路由约定（src/app/routes/）：
 *   - 每个 tsx 文件是「约定式路由」的薄壳，1-3 行 re-export 到 features 对应 ui；
 *   - __layout.tsx 是 Next.js 风格的根 layout，把 Outlet 放进 MobileShell；
 *   - routeTree.tsx 用 import.meta.glob 扫所有 shell，产出 react-router 路由树。
 *
 * App 层只负责：
 *   - 顶层 providers（QueryClient / AppStore / Sync / Toast）；
 *   - 把 /login 放在 layout 之外、未登录态全屏；
 *   - 把 layout 内的所有路由交给 routeTree。
 */

import React, { useEffect } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { AppStoreProvider } from '../shared/lib/state/store';
import { SyncBootstrap } from '../shared/ui/SyncBootstrap';
import { ToastHost } from '../shared/ui/ToastHost';
import { ThemeProvider } from '../shared/theme/theme';
import { IconStyleProvider } from '../shared/ui/iconStyle';

import { routeTree, loginRoute } from './routes/routeTree';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 1000 * 60 * 5, retry: 1 } },
});

const BootSplashDismissal: React.FC = () => {
  useEffect(() => {
    document.getElementById('boot-splash')?.remove();
  }, []);
  return null;
};

const router = createBrowserRouter([
  loginRoute,
  ...routeTree,
  { path: '*', element: <Navigate to="/home" replace /> },
]);

const Providers: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <AppStoreProvider>
      <ThemeProvider>
        <IconStyleProvider>
          <BootSplashDismissal />
          <RouterProvider router={router} future={{ v7_startTransition: true }} />
        </IconStyleProvider>
      </ThemeProvider>
      <SyncBootstrap />
      <ToastHost />
    </AppStoreProvider>
  </QueryClientProvider>
);

export default function App() {
  return <Providers />;
}
